import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ToastrService } from 'ngx-toastr';
import * as requestsActions from './requests.actions';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap, filter } from 'rxjs/operators';
import * as moment from 'moment';
import { DashboardCamelCasePipe } from 'src/app/core/pipes/dashboard-camel-case/dashboard-camel-case.pipe';
import { RequestsService } from '../../services/requests/requests.service';
import { ResearcherRequest } from 'src/app/models/researcher-request';
import { CustomRouterNavigatedAction } from 'src/app/core/custom-route-serializer';
import { ROUTER_NAVIGATED } from '@ngrx/router-store';
import { SETTINGS_ROUTES } from '../../settings/settings.routes';

@Injectable()
export class RequestsEffects {
  researcherRequestsRoute$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CustomRouterNavigatedAction>(ROUTER_NAVIGATED),
      filter((route: CustomRouterNavigatedAction) => {
        return route.payload.routerState.path === SETTINGS_ROUTES.REQUESTS;
      }),
      map(() => requestsActions.getRequests())
    )
  );

  getRequests$ = createEffect(() =>
    this.actions$.pipe(
      ofType(requestsActions.getRequests),
      switchMap(() => {
        return this.requestsService.getRequests().pipe(
          map((requests) => {
            const transformedRequests = requests.map((request) => {
              const transformedRequest: ResearcherRequest = this.dashboardCamelCasePipe.keysToCamelCase(request);
              transformedRequest.registeredAt = moment(transformedRequest.registeredAt).format('DD/MM/YYYY');
              return transformedRequest;
            });
            return requestsActions.getRequestsSuccess({
              requests: transformedRequests,
            });
          }),
          catchError(() => of(requestsActions.getRequestsError()))
        );
      })
    )
  );

  getRequestsError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(requestsActions.getRequestsError),
        tap(() => this.toastrService.error('Error in fetching Researcher Requests'))
      ),
    { dispatch: false }
  );

  acceptRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(requestsActions.acceptRequest),
      switchMap(({ request }) => {
        return this.requestsService.acceptRequest(request.id, request.studyId).pipe(
          map(() => requestsActions.acceptRequestSuccess({ acceptedRequest: request })),
          catchError((error) => of(requestsActions.acceptRequestError(error)))
        );
      })
    )
  );

  acceptRequestsSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(requestsActions.acceptRequestSuccess),
        tap(() => this.toastrService.success('Researcher successfully accepted'))
      ),
    { dispatch: false }
  );

  acceptRequestsError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(requestsActions.acceptRequestError),
        tap((error) =>
          this.toastrService.error(
            error.hasOwnProperty('error') ? error.error.toString() : 'Unable to accept Researcher'
          )
        )
      ),
    { dispatch: false }
  );

  rejectRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(requestsActions.rejectRequest),
      switchMap(({ request }) => {
        return this.requestsService.rejectRequest(request.id, request.studyId).pipe(
          map(() => requestsActions.rejectRequestSuccess({ rejectedRequest: request })),
          catchError((error) => of(requestsActions.rejectRequestError(error)))
        );
      })
    )
  );

  rejectRequestsSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(requestsActions.rejectRequestSuccess),
        tap(() => this.toastrService.success('Researcher successfully rejected'))
      ),
    { dispatch: false }
  );

  rejectRequestsError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(requestsActions.rejectRequestError),
        tap((error) =>
          this.toastrService.error(
            error.hasOwnProperty('error') ? error.error.toString() : 'Unable to reject Researcher'
          )
        )
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private requestsService: RequestsService,
    private toastrService: ToastrService,
    private dashboardCamelCasePipe: DashboardCamelCasePipe
  ) {}
}
